import clsx from 'clsx';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { CHOSEN } from './primitives/controls.tsx';

export function Toolbar({ children }: { children: ReactNode }) {
  return (
    <div className="no-scrollbar edge-fade flex shrink-0 items-center gap-3 overflow-x-auto border-b border-line-soft px-gutter py-2 sm:flex-wrap sm:py-2.5">
      {children}
    </div>
  );
}

export function ToolbarLine({ children }: { children: ReactNode }) {
  return <div className="flex shrink-0 items-center gap-2">{children}</div>;
}

function SegmentTrack({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <fieldset
      aria-label={label}
      className={clsx(
        'group inline-flex min-w-0 gap-0.5 rounded-track border border-line-soft p-0.5',
        className ?? 'bg-surface-raised',
      )}
    >
      {children}
    </fieldset>
  );
}

function Segment({
  on,
  onClick,
  className,
  children,
}: {
  on: boolean;
  onClick: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={clsx(
        'flex items-center gap-1.5 rounded-control px-2.5 py-0.5 text-xs whitespace-nowrap transition-colors touch:min-h-7 touch:px-3',
        on
          ? clsx(className ?? 'bg-surface', 'text-ink ring-1 ring-ink/40')
          : 'text-ink-muted hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

/**
 * `labels` words an option; unworded, the option is its own label.
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
  labels,
  className,
  onClassName,
}: {
  options: readonly T[];
  value: T;
  onChange: (next: T) => void;
  label: string;
  labels?: Partial<Record<T, ReactNode>>;
  className?: string;
  onClassName?: string;
}) {
  return (
    <SegmentTrack label={label} className={className}>
      {options.map((option) => (
        <Segment
          key={option}
          on={option === value}
          onClick={() => onChange(option)}
          className={onClassName}
        >
          {labels?.[option] ?? <span className="capitalize">{option}</span>}
        </Segment>
      ))}
    </SegmentTrack>
  );
}

export interface ChipEntry {
  value: string | null;
  label: string;
  count?: number;
  title?: string;
  muted?: boolean;
  tint?: string;
  clears?: boolean;
}

export function ChipRow({
  entries,
  selected,
  onSelect,
  label,
}: {
  entries: ChipEntry[];
  selected: string | null;
  onSelect: (value: string | null) => void;
  label: string;
}) {
  return (
    <fieldset
      aria-label={label}
      className="flex shrink-0 items-center gap-1.5 sm:min-w-0 sm:shrink sm:flex-wrap"
    >
      {entries.map((entry) => {
        const on = entry.value === selected;
        return (
          <button
            // No value is '*': a chip is a book or analyst id, a category, a kind or a symbol.
            key={entry.value ?? '*'}
            type="button"
            aria-pressed={on}
            title={entry.title}
            onClick={() => onSelect(on ? null : entry.value)}
            className={clsx(
              'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs whitespace-nowrap touch:min-h-7 touch:px-3',
              on ? CHOSEN : 'border-line-soft text-ink-muted hover:text-ink',
              entry.count === 0 && !on && 'opacity-60',
              entry.muted && 'line-through decoration-ink-faint',
            )}
            style={
              on && entry.tint
                ? {
                    borderColor: `color-mix(in oklch, ${entry.tint} 55%, transparent)`,
                    backgroundColor: `color-mix(in oklch, ${entry.tint} 14%, transparent)`,
                  }
                : undefined
            }
          >
            {entry.label}
            {entry.count !== undefined && (
              <span className="tnum text-xs text-ink-faint">{entry.count}</span>
            )}
            {entry.clears && on && <X aria-hidden className="-mr-1 size-3 text-ink-faint" />}
          </button>
        );
      })}
    </fieldset>
  );
}
